import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateWorkspace } from '@/entities/collab';
import type { Workspace } from '@/entities/workspace';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { InlineTitleEditor } from '@/shared/ui';

import styles from './WorkspaceTitle.module.css';

const NAME_MAX = 100;

/**
 * Workspace title: authorized users (``workspace.update_settings``) click it to rename in
 * place - Enter saves, Escape cancels, leaving the field saves a changed non-empty name and a
 * failed save keeps the typed text with the error. Everyone else sees static text.
 */
export function WorkspaceTitle({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const update = useUpdateWorkspace(workspace.id);
  const [editing, setEditing] = useState(false);
  const canEdit =
    workspace.workspace_type === 'team' &&
    can(workspace.permissions, PERMISSIONS.workspaceUpdateSettings);

  if (editing) {
    return (
      <div className={styles.edit}>
        <InlineTitleEditor
          name={workspace.name}
          label={t('workspace.nameInline')}
          className={styles.input}
          maxLength={NAME_MAX}
          onSave={(name) => update.mutateAsync({ name })}
          onClose={() => {
            setEditing(false);
          }}
        />
      </div>
    );
  }
  if (!canEdit) return <h1 className={styles.title}>{workspace.name}</h1>;
  return (
    <h1 className={styles.title}>
      <span
        role="button"
        tabIndex={0}
        className={styles.button}
        title={t('workspace.renameHint')}
        onClick={() => {
          setEditing(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setEditing(true);
          }
        }}
      >
        {workspace.name}
      </span>
    </h1>
  );
}
